<script setup lang="ts">
import UiAccordion from '@boats/ui/components/UiAccordion.vue'
import { fetchGiftCardProductData, queryKey, type InfoData } from '~/queries'

const props = defineProps<{
  breadcrumbs: InfoData['breadcrumbs']
  product: InfoData['product']
  refresh: () => Promise<unknown>
}>()

const { $i18n } = useNuxtApp()
const { t } = $i18n
const cart = useCartStore()

const { data: giftCardConfig } = await useAsyncData(
  queryKey('gift-card-product'),
  fetchGiftCardProductData,
)

const breadcrumbsItems = computed(() =>
  props.breadcrumbs.map((item) => ({
    title: item.title,
    href: cleanPath('/', item.path),
  })),
)

const resolvedGiftCardConfig = computed(() => giftCardConfig.value ?? null)

const presetAmounts = computed(() =>
  resolvedGiftCardConfig.value?.presetValues ?? [],
)

const formatGiftCardCurrency = (value: number) => {
  const currency = resolvedGiftCardConfig.value?.currency

  if (!currency) return value.toString()

  return new Intl.NumberFormat(getLocale($i18n.locale.value), {
    style: 'currency',
    currency: currency.code,
    minimumFractionDigits: currency.precision ?? 0,
    maximumFractionDigits: currency.precision ?? 0,
  }).format(value)
}

type GiftCardDraft = {
  selectedPreset: number | null
  customAmount: string
  recipientName: string
  recipientEmail: string
  message: string
}

const giftCardDraftStorageKey = computed(() =>
  `gift-card-draft:${props.product?.id ?? props.product?.path ?? 'default'}:${resolvedGiftCardConfig.value?.currency.code ?? 'default'}`,
)

const selectedPreset = ref<number | null>(presetAmounts.value[0] ?? null)
const customAmount = ref('')
const recipientName = ref('')
const recipientEmail = ref('')
const message = ref('')
const submitting = ref(false)
const submitted = ref(false)

const amount = computed(() => {
  if (customAmount.value.trim()) {
    return Number(customAmount.value)
  }

  return selectedPreset.value ?? null
})

const amountError = computed(() => {
  if (!submitted.value) return ''

  if (!amount.value || Number.isNaN(amount.value)) {
    return t('page.gift-card.purchase.error.amount')
  }

  if (
    resolvedGiftCardConfig.value?.minValue &&
    amount.value < resolvedGiftCardConfig.value.minValue
  ) {
    return t('page.gift-card.purchase.error.min', {
      amount: formatGiftCardCurrency(resolvedGiftCardConfig.value.minValue),
    })
  }

  if (
    resolvedGiftCardConfig.value?.maxValue &&
    amount.value > resolvedGiftCardConfig.value.maxValue
  ) {
    return t('page.gift-card.purchase.error.max', {
      amount: formatGiftCardCurrency(resolvedGiftCardConfig.value.maxValue),
    })
  }

  return ''
})

const recipientNameError = computed(() => {
  if (!submitted.value || recipientName.value.trim()) return ''

  return t('page.gift-card.purchase.error.recipient-name')
})

const recipientEmailError = computed(() => {
  if (!submitted.value || !recipientEmail.value.trim()) return ''

  const emailSchema = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  return emailSchema.test(recipientEmail.value.trim())
    ? ''
    : t('page.gift-card.purchase.error.recipient-email')
})

const formError = computed(
  () =>
    [amountError.value, recipientNameError.value, recipientEmailError.value].find(
      Boolean,
    ) || '',
)

const faqs = computed(() => {
  const items = []

  if (props.product?.notes) {
    items.push({
      title: t('page.product.notes.title'),
      content: props.product.notes,
    })
  }

  if (props.product?.additionalDescription) {
    items.push({
      title: t('page.product.additionalDescription.title'),
      content: props.product.additionalDescription,
    })
  }

  if (props.product?.additionalNotes) {
    items.push({
      title: t('page.product.additionalNotes.title'),
      content: props.product.additionalNotes,
    })
  }

  props.product?.faqs
    ?.sort((a, b) => a.position - b.position)
    .forEach((faq) => {
      items.push({
        title: faq.question,
        content: faq.answer,
      })
    })

  return items
})

const currentGiftCardCartItem = computed(() =>
  cart.data?.items?.find((item) => item.type === 'gift_card' && item.productId === props.product.id) ?? null,
)

const onCustomAmountInput = (value: string) => {
  customAmount.value = value.replace(/[^\d.]/g, '')

  if (customAmount.value.trim()) {
    selectedPreset.value = null
  }
}

const selectPresetAmount = (value: number) => {
  selectedPreset.value = value
  customAmount.value = ''
}

const saveDraft = () => {
  if (!import.meta.client) return

  const draft: GiftCardDraft = {
    selectedPreset: selectedPreset.value,
    customAmount: customAmount.value,
    recipientName: recipientName.value,
    recipientEmail: recipientEmail.value,
    message: message.value,
  }

  const serializedDraft = JSON.stringify(draft)

  window.sessionStorage.setItem(giftCardDraftStorageKey.value, serializedDraft)
  window.localStorage.setItem(giftCardDraftStorageKey.value, serializedDraft)
}

const loadDraft = () => {
  if (!import.meta.client) return

  const rawDraft =
    window.sessionStorage.getItem(giftCardDraftStorageKey.value) ||
    window.localStorage.getItem(giftCardDraftStorageKey.value)
  if (!rawDraft) return

  try {
    const draft = JSON.parse(rawDraft) as Partial<GiftCardDraft>

    selectedPreset.value =
      typeof draft.selectedPreset === 'number' ? draft.selectedPreset : selectedPreset.value
    customAmount.value = typeof draft.customAmount === 'string' ? draft.customAmount : ''
    recipientName.value = typeof draft.recipientName === 'string' ? draft.recipientName : ''
    recipientEmail.value = typeof draft.recipientEmail === 'string' ? draft.recipientEmail : ''
    message.value = typeof draft.message === 'string' ? draft.message : ''

    if (customAmount.value.trim()) {
      selectedPreset.value = null
    }
  } catch {
    window.sessionStorage.removeItem(giftCardDraftStorageKey.value)
    window.localStorage.removeItem(giftCardDraftStorageKey.value)
  }
}

const hydrateDraftFromCartItem = () => {
  const cartItem = currentGiftCardCartItem.value
  if (!cartItem?.giftCard) return

  if (!recipientName.value.trim()) {
    recipientName.value = cartItem.giftCard.recipientName || ''
  }

  if (!recipientEmail.value.trim()) {
    recipientEmail.value = cartItem.giftCard.recipientEmail || ''
  }

  if (!message.value.trim()) {
    message.value = cartItem.giftCard.message || ''
  }

  if (!customAmount.value.trim() && selectedPreset.value === null && cartItem.giftCard.value) {
    selectedPreset.value = cartItem.giftCard.value
  }
}

watch(presetAmounts, (amounts) => {
  if (!amounts.length) return

  if (selectedPreset.value === null && !customAmount.value.trim()) {
    selectedPreset.value = amounts[0]
  }
})

watch(
  [selectedPreset, customAmount, recipientName, recipientEmail, message],
  saveDraft,
)

onMounted(() => {
  loadDraft()
  hydrateDraftFromCartItem()
})

const onAddToCart = async () => {
  submitted.value = true

  if (!props.product || !resolvedGiftCardConfig.value || formError.value) return

  submitting.value = true

  try {
    if (currentGiftCardCartItem.value?.id) {
      await cart.removeFromCart(currentGiftCardCartItem.value.id, { showToast: false })
    }

    await cart.addToCart({
      productId: resolvedGiftCardConfig.value.productId,
      variations: [{ quantity: 1 }],
      giftCard: {
        value: Number(amount.value),
        recipient_name: recipientName.value.trim(),
        recipient_email: recipientEmail.value.trim() || undefined,
        message: message.value.trim() || undefined,
      },
    })
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <AppContainer v-if="product" class="my-10 md:my-20">
    <div class="row">
      <div class="col-lg-5 mb-8 lg:mb-0 xl:pr-16">
        <AppCarouselMedia :object="product" type="product" />

        <div
          v-if="product.ticketSupplierDescription"
          class="mt-6 flex items-start rounded border border-gray-200 p-3 md:p-6"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 36 42"
            class="mr-3 w-[1.875rem] flex-shrink-0 md:mr-6 md:w-9"
          >
            <path
              d="M.68,7a1,1,0,0,1,.48-.36h0l16.5-6h0a1,1,0,0,1,.68,0h0l16.5,6h0a1,1,0,0,1,.48.36,1,1,0,0,1,.18.57h0V21c0,7-4.06,12-8.28,15.3a34.83,34.83,0,0,1-8.88,5.06h0a1,1,0,0,1-.68,0h0A34.68,34.68,0,0,1,8.79,36.3C4.56,33,.5,28,.5,21V7.55A1,1,0,0,1,.68,7"
              fill="#e0f0ff"
              stroke="#121318"
            />
            <path
              d="M11.67,19.37l4.17,4.06,9.46-10.7"
              fill="none"
              stroke="#121318"
              stroke-width="2"
            />
          </svg>

          <div
            class="ticket-supplier-description"
            v-html="product.ticketSupplierDescription"
          ></div>
        </div>

        <div class="gift-card-lookup-card mt-6">
          <p class="text-button mb-2">{{ $t('page.gift-card.purchase.lookup.title') }}</p>
          <p class="mb-4">{{ $t('page.gift-card.purchase.lookup.description') }}</p>
          <AppButton
            to="/gift-cards/check"
            variant="outlined"
            class="gift-card-secondary-button"
          >
            {{ $t('page.gift-card.purchase.lookup.button') }}
          </AppButton>
        </div>
      </div>

      <div class="col-lg-7 col-xl-6">
        <div class="hidden md:block">
          <AppBreadcrumbs class="!mb-3" :items="breadcrumbsItems" />
        </div>

        <h1 class="gift-card-title">{{ product.title }}</h1>

        <div
          v-if="product.intro"
          class="lead clean-cms-styles mb-6 md:mb-8"
          v-html="product.intro"
        ></div>

        <AppPreviewContent v-if="product.description">
          <div
            class="clean-cms-styles mb-6 md:mb-8"
            v-html="product.description"
          ></div>
        </AppPreviewContent>

        <AppPaper v-if="resolvedGiftCardConfig" class="gift-card-form p-4 pt-6 md:p-8">
          <div class="mb-6">
            <p class="text-button mb-2">{{ $t('page.gift-card.purchase.amount.title') }}</p>
            <p class="mb-4">{{ $t('page.gift-card.purchase.amount.description') }}</p>
            <p class="mb-4">{{ $t('page.gift-card.currency.purchase', { currency: resolvedGiftCardConfig?.currency.code }) }}</p>

            <div class="gift-card-presets mb-4">
              <button
                v-for="preset in presetAmounts"
                :key="preset"
                type="button"
                class="gift-card-presets__button"
                :class="{ 'is-active': selectedPreset === preset && !customAmount }"
                @click="selectPresetAmount(preset)"
              >
                {{ formatGiftCardCurrency(preset) }}
              </button>
            </div>

            <AppTextField
              :model-value="customAmount"
              :placeholder="$t('page.gift-card.purchase.amount.custom-placeholder')"
              full-width
              :error="!!amountError"
              @update:model-value="onCustomAmountInput"
            />

            <p
              v-if="resolvedGiftCardConfig?.minValue || resolvedGiftCardConfig?.maxValue"
              class="gift-card-help"
            >
              {{
                $t('page.gift-card.purchase.amount.range', {
                  min: resolvedGiftCardConfig?.minValue
                    ? formatGiftCardCurrency(resolvedGiftCardConfig.minValue)
                    : '0',
                  max: resolvedGiftCardConfig?.maxValue
                    ? formatGiftCardCurrency(resolvedGiftCardConfig.maxValue)
                    : '0',
                })
              }}
            </p>
            <p v-if="amountError" class="gift-card-error">{{ amountError }}</p>
          </div>

          <div class="mb-6">
            <p class="text-button mb-2">{{ $t('page.gift-card.purchase.recipient.title') }}</p>
            <p class="mb-4">{{ $t('page.gift-card.purchase.recipient.description') }}</p>

            <AppTextField
              v-model="recipientName"
              :placeholder="$t('page.gift-card.purchase.recipient.name')"
              full-width
              class="mb-3"
              :error="!!recipientNameError"
            />
            <p v-if="recipientNameError" class="gift-card-error">{{ recipientNameError }}</p>

            <AppTextField
              v-model="recipientEmail"
              :placeholder="$t('page.gift-card.purchase.recipient.email')"
              type="email"
              full-width
              class="mb-3"
              :error="!!recipientEmailError"
            />
            <p class="gift-card-help mb-3">
              {{ $t('page.gift-card.purchase.recipient.email-help') }}
            </p>
            <p v-if="recipientEmailError" class="gift-card-error">{{ recipientEmailError }}</p>

            <AppTextField
              v-model="message"
              :placeholder="$t('string.Message')"
              multiline
              :rows="5"
              full-width
            />
          </div>

          <div class="gift-card-summary mb-6">
            <span>{{ $t('page.gift-card.purchase.summary') }}</span>
            <strong>{{ amount !== null ? formatGiftCardCurrency(Number(amount)) : '' }}</strong>
          </div>

          <AppButton
            full-width
            class="gift-card-primary-button"
            :loading="submitting"
            @click="onAddToCart"
          >
            {{ $t('page.gift-card.purchase.button') }}
          </AppButton>
        </AppPaper>
        <AppPaper v-else class="p-4 md:p-8">
          <p>{{ $t('page.gift-card.purchase.unavailable') }}</p>
        </AppPaper>
      </div>
    </div>
  </AppContainer>

  <AppContainer v-if="product" class="my-10 md:my-20">
    <template v-if="product.body || faqs.length">
      <AppDivider class="my-8 md:my-16" strong />

      <section class="row">
        <div class="col-lg-3 mb-6 lg:mb-0">
          <h2 class="alt">{{ $t('page.product.body.title') }}</h2>
        </div>

        <div class="col-lg-9 col-xl-8">
          <div v-if="product.body" class="mb-8">
            <AppPreviewContent>
              <div class="cms-content clean-cms-styles" v-html="product.body"></div>
            </AppPreviewContent>
          </div>

          <UiAccordion v-if="faqs.length" :items="faqs" />
        </div>
      </section>
    </template>

    <AppDivider class="my-8 md:my-16" strong />

    <AppNewsletter />
  </AppContainer>
</template>

<style scoped lang="scss">
@import '@boats/ui/styles/config';

.gift-card-title {
  font-size: 3rem;
  margin-bottom: 0.75rem;
}

.gift-card-form {
  background-color: $white;
  border: 1px solid $black;
}

.gift-card-lookup-card {
  padding: 1.5rem;
  border: 1px solid $gray-200;
  border-radius: 0.25rem;
  background-color: $white;
}

.gift-card-presets {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.gift-card-presets__button {
  padding: 1rem 1.25rem;
  color: $red;
  background-color: $white;
  border: 1px solid $red;
  border-radius: 0.25rem;
  font-family: $font-barlow;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.42857143;
  letter-spacing: 0.08333333em;
  text-transform: uppercase;
  transition:
    color 0.15s ease,
    border-color 0.15s ease,
    background-color 0.15s ease;

  &.is-active,
  &:hover {
    color: $white;
    background-color: $red;
    border-color: $red;
  }
}

.gift-card-help {
  margin-top: 0.75rem;
  color: $gray-500;
}

.gift-card-error {
  margin-top: 0.5rem;
  color: $signal-error-red;
  font-size: 0.875rem;
}

.gift-card-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-top: 1px solid $gray-200;
  border-bottom: 1px solid $gray-200;
  font-family: $font-poppins;
}

.gift-card-primary-button.app-button--contained {
  background-color: $red;
  border-color: $red;

  &:hover,
  &:focus {
    background-color: $tint-red-hover;
    border-color: $tint-red-hover;
  }
}

.gift-card-secondary-button.app-button--outlined {
  color: $red;
  border-color: $red;

  &:hover,
  &:focus,
  &.app-button--active {
    color: $white;
    background-color: $red;
    border-color: $red;
  }
}

::v-deep(.ticket-supplier-description h4) {
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
}

@include media-breakpoint-up(md) {
  .gift-card-presets {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  ::v-deep(.ticket-supplier-description h4) {
    font-size: 1.25rem;
  }
}
</style>
